/**
 * UniversalFilterBar — shared filter chip strip used across all
 * Procurement tabs. Presents a consistent set of filter controls so
 * every tab (Register, Pre-Order, Post-Order, Delivery Forecast,
 * Vendor Book, PO Performance, Long Lead) filters the same way.
 *
 * Any prop that is `undefined` is HIDDEN so tabs can opt in only to
 * the filters they need. Values are controlled by the parent.
 */
import { useMemo } from 'react';
import { Search, X, Filter as FilterIcon } from 'lucide-react';

const CHIP = {
  base: 'px-2.5 py-1 text-[11px] font-semibold rounded-md border transition-colors',
};

function ChipGroup({ label, testId, value, options, onChange }) {
  if (!options) return null;
  return (
    <div className="flex items-center gap-1" data-testid={`ufb-group-${testId}`}>
      <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button key={opt.value}
              onClick={() => onChange(opt.value)}
              className="px-2.5 py-1 text-[11px] font-semibold transition-colors"
              style={{
                background: active ? '#1E4E8C' : 'var(--card-bg)',
                color: active ? '#ffffff' : 'var(--text-muted)',
              }}
              data-testid={`ufb-chip-${testId}-${String(opt.value).toLowerCase()}`}>
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextFilter({ label, testId, value, onChange, placeholder }) {
  if (value === undefined) return null;
  return (
    <div className="flex items-center gap-1" data-testid={`ufb-text-${testId}`}>
      <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        className="w-28 px-2 py-1 rounded-md text-[11px] border"
        style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
        data-testid={`ufb-input-${testId}`}
      />
    </div>
  );
}

export default function UniversalFilterBar({
  // search box (universal)
  search, onSearchChange, searchPlaceholder = 'Search…',
  // chip filters (only rendered when options provided)
  category, onCategoryChange, categoryOptions,
  priority, onPriorityChange, priorityOptions,
  phase, onPhaseChange, phaseOptions,
  discipline, onDisciplineChange, disciplineOptions,
  gate, onGateChange, gateOptions,
  uom, onUomChange, uomOptions,
  // text filters — always rendered as text inputs when the value is a string
  mr, onMrChange,
  po, onPoChange,
  poLine, onPoLineChange,
  vendor, onVendorChange,
  // trailing actions (e.g., Refresh, Add, Cognara button)
  right,
  totalCount,
  totalLabel = 'items',
}) {
  // Which chip filters are present? (used for the reset button)
  const active = useMemo(() => {
    const list = [];
    if (search) list.push('search');
    if (category && category !== 'All' && category !== 'ALL') list.push('category');
    if (priority && priority !== 'All' && priority !== 'ALL') list.push('priority');
    if (phase && phase !== 'All' && phase !== 'ALL') list.push('phase');
    if (discipline && discipline !== 'All' && discipline !== 'ALL') list.push('discipline');
    if (gate && gate !== 'All' && gate !== 'ALL') list.push('gate');
    if (mr) list.push('mr'); if (po) list.push('po'); if (poLine) list.push('poLine'); if (vendor) list.push('vendor');
    return list;
  }, [search, category, priority, phase, discipline, gate, mr, po, poLine, vendor]);

  const resetAll = () => {
    onSearchChange?.('');
    if (categoryOptions)   onCategoryChange?.(categoryOptions[0]?.value ?? 'All');
    if (priorityOptions)   onPriorityChange?.(priorityOptions[0]?.value ?? 'All');
    if (phaseOptions)      onPhaseChange?.(phaseOptions[0]?.value ?? 'All');
    if (disciplineOptions) onDisciplineChange?.(disciplineOptions[0]?.value ?? 'All');
    if (gateOptions)       onGateChange?.(gateOptions[0]?.value ?? 'All');
    if (mr !== undefined)      onMrChange?.('');
    if (po !== undefined)      onPoChange?.('');
    if (poLine !== undefined)  onPoLineChange?.('');
    if (vendor !== undefined)  onVendorChange?.('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg border"
      style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
      data-testid="universal-filter-bar">
      {/* Search */}
      {onSearchChange && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search || ''} onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-7 pr-2 py-1 rounded-md text-[11px] border w-52"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="ufb-search" />
        </div>
      )}
      <ChipGroup label="Category"   testId="category"   value={category}   options={categoryOptions}   onChange={onCategoryChange} />
      <ChipGroup label="Priority"   testId="priority"   value={priority}   options={priorityOptions}   onChange={onPriorityChange} />
      <ChipGroup label="Phase"      testId="phase"      value={phase}      options={phaseOptions}      onChange={onPhaseChange} />
      <ChipGroup label="Discipline" testId="discipline" value={discipline} options={disciplineOptions} onChange={onDisciplineChange} />
      <ChipGroup label="Gate"       testId="gate"       value={gate}       options={gateOptions}       onChange={onGateChange} />
      <ChipGroup label="UoM"        testId="uom"        value={uom}        options={uomOptions}        onChange={onUomChange} />

      <TextFilter label="MR"      testId="mr"      value={mr}      onChange={onMrChange} placeholder="MR-…" />
      <TextFilter label="PO"      testId="po"      value={po}      onChange={onPoChange} placeholder="PO-…" />
      <TextFilter label="PO Line" testId="poline"  value={poLine}  onChange={onPoLineChange} placeholder="line #" />
      <TextFilter label="Vendor"  testId="vendor"  value={vendor}  onChange={onVendorChange} placeholder="Vendor name" />

      {active.length > 0 && (
        <button onClick={resetAll}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md border"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}
          data-testid="ufb-reset">
          <X className="w-3 h-3" /> Reset {active.length}
        </button>
      )}

      <div className="ml-auto flex items-center gap-2">
        {totalCount != null && (
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}
            data-testid="ufb-total">{totalCount} {totalLabel}</span>
        )}
        {right}
      </div>
    </div>
  );
}
